import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { MarketComparisonData } from '../../types/report';

interface MarketComparisonChartProps {
  data: MarketComparisonData[];
}

export const MarketComparisonChart = ({ data }: MarketComparisonChartProps) => {
  const chartData = data.map((item) => ({
    category: item.category,
    dalockAvg: item.dalockAvg,
    marketMin: item.marketRangeMin || 0,
    marketMax: item.marketRangeMax || 0,
    marketAvg: item.marketAvg || 0,
    evaluation: item.evaluation,
  }));

  const formatValue = (value: number, category: string) => {
    if (category.includes('할인율')) {
      return `${value}%`;
    }
    return `${(value / 10000).toFixed(1)}만원`;
  };

  return (
    <div className="space-y-6">
      {/* 테이블 형식 */}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 font-medium text-left text-gray-500">
                비교 항목
              </th>
              <th className="px-4 py-3 font-medium text-right text-gray-500">
                다락 전사 평균
              </th>
              <th className="px-4 py-3 font-medium text-right text-gray-500">
                시장 범위/평균
              </th>
              <th className="px-4 py-3 font-medium text-left text-gray-500">
                평가
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.map((row, idx) => (
              <tr key={idx}>
                <td className="px-4 py-3 font-medium text-gray-900">
                  {row.category}
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="font-semibold text-blue-600">
                    {row.category.includes('할인율')
                      ? `${row.dalockAvg}%`
                      : `${row.dalockAvg.toLocaleString()}원`}
                  </span>
                </td>
                <td className="px-4 py-3 text-right text-gray-600">
                  {row.marketRangeMin && row.marketRangeMax
                    ? `${row.marketRangeMin.toLocaleString()}원 ~ ${row.marketRangeMax.toLocaleString()}원`
                    : row.marketAvg
                      ? `평균 ${row.marketAvg}%`
                      : '-'}
                </td>
                <td className="px-4 py-3 max-w-md text-sm text-gray-600">
                  {row.evaluation}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 시각적 비교 차트 - 가격 항목만 */}
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData.filter((d) => !d.category.includes('할인율'))}
            layout="vertical"
            margin={{ top: 20, right: 30, left: 140, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              type="number"
              tickFormatter={(value) => `${(value / 10000).toFixed(0)}만`}
              domain={[0, 'auto']}
            />
            <YAxis
              type="category"
              dataKey="category"
              width={130}
              tick={{ fontSize: 12 }}
            />
            <Tooltip
              content={({ active, payload, label }: any) => {
                if (active && payload && payload.length) {
                  const item = data.find((d) => d.category === label);
                  return (
                    <div className="p-4 bg-white rounded-lg border shadow-lg">
                      <p className="mb-2 font-semibold text-gray-900">
                        {label}
                      </p>
                      {payload.map((entry: any, index: number) => (
                        <p
                          key={index}
                          style={{ color: entry.color }}
                          className="text-sm"
                        >
                          {entry.name}: {formatValue(entry.value, label)}
                        </p>
                      ))}
                      {item?.evaluation && (
                        <p className="pt-2 mt-2 max-w-xs text-xs text-gray-500 border-t">
                          {item.evaluation}
                        </p>
                      )}
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend />
            <Bar
              dataKey="marketMin"
              name="시장 최소"
              fill="#a78bfa"
              stackId="market"
            />
            <Bar dataKey="marketMax" name="시장 최대" fill="#6d28d9" />
            <Bar dataKey="dalockAvg" name="다락 평균" fill="#3b82f6">
              {chartData
                .filter((d) => !d.category.includes('할인율'))
                .map((entry, index) => {
                  const isAboveMarket =
                    entry.marketMax && entry.dalockAvg > entry.marketMax;
                  const isBelowMarket =
                    entry.marketMin && entry.dalockAvg < entry.marketMin;
                  return (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        isAboveMarket
                          ? '#22c55e'
                          : isBelowMarket
                            ? '#ef4444'
                            : '#3b82f6'
                      }
                    />
                  );
                })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* 할인율 비교 */}
      <div className="flex gap-8 justify-center items-center p-6 bg-gray-50 rounded-lg">
        <div className="text-center">
          <p className="mb-1 text-sm text-gray-500">다락 평균 할인율</p>
          <p className="text-3xl font-bold text-red-600">
            {chartData.find((d) => d.category.includes('할인율'))?.dalockAvg ||
              0}
            %
          </p>
        </div>
        <div className="text-4xl text-gray-300">vs</div>
        <div className="text-center">
          <p className="mb-1 text-sm text-gray-500">업계 평균 할인율</p>
          <p className="text-3xl font-bold text-green-600">
            {chartData.find((d) => d.category.includes('할인율'))?.marketAvg ||
              15}
            %
          </p>
        </div>
        <div className="pl-8 text-center border-l">
          <p className="mb-1 text-sm text-gray-500">차이</p>
          <p className="text-3xl font-bold text-orange-600">
            +
            {(
              (chartData.find((d) => d.category.includes('할인율'))
                ?.dalockAvg || 0) -
              (chartData.find((d) => d.category.includes('할인율'))
                ?.marketAvg || 15)
            ).toFixed(1)}
            %p
          </p>
        </div>
      </div>
    </div>
  );
};
